import React, { useState, ReactElement } from 'react'
import { CircularProgress, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

import Headline from '@objects/headline'
import Button from '@objects/button'
import Icon from '@objects/icon'

import { Colors } from '@config/theme/palette'
import FontSize from '@config/theme/definitions/fontSize'

import { sendMessage } from '@services/api/autobahnVotingPhase1'

import { useAutobahnplakateTexts } from '../store'
import { SWIPER_SPEED_MS } from '../index'

const useStyles = makeStyles((theme) => ({
  root: {},
  headline: {
    fontWeight: 900,
    fontSize: '1.5rem',
    textAlign: 'center',
    margin: 0,
    '& .highlight': {
      color: Colors.green[500],
    },

    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },

    [theme.breakpoints.up('lg')]: {
      fontSize: '2rem',
    },
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  copy: {
    fontSize: FontSize['2lg'],
    textAlign: 'center' as const,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),

    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(10),
    },

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },

    '& button': {
      alignSelf: 'center',
    },
  },

  hideLabel: {
    opacity: 0,
  },
  spinner: {
    color: theme.palette.button.mainText,
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0,
  },
  showSpinner: {
    opacity: 1,
  },
  skipButton: {
    border: `2px solid ${theme.palette.button.main} !important`,
  },
  textAreaRoot: {
    position: 'relative',
  },
  textAreaInput: {
    fontFamily: theme.typography.fontFamily2,
    fontSize: FontSize['lg'],
    border: `2px solid #798414`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4, 2, 2, 14),
    width: '100%',
    height: '317px',
    resize: 'none',

    '&::placeholder': {
      color: theme.palette.button.disabled,
    },

    '&:focus': {
      outline: 'none',
    },
  },
  textAreaInputError: {
    borderColor: theme.palette.error.dark,
  },
  textAreaIcon: {
    position: 'absolute',
    top: theme.spacing(5),
    left: theme.spacing(6),
    fontSize: '21px',
    color: '#798414',
  },
  error: {
    color: theme.palette.error.dark,
    fontWeight: 500,
    margin: 0,
  },
  label: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}))

export type MessageProps = Record<string, never>

export default function Message(): ReactElement {
  const classes = useStyles()
  const store = useAutobahnplakateTexts()
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState(false)
  const errorId = 'message-textarea-error'

  const items = {
    OPTION_1: store.votingItemLabel1,
    OPTION_2: store.votingItemLabel2,
    OPTION_3: store.votingItemLabel3,
    OPTION_4: store.votingItemLabel4,
    OPTION_5: store.votingItemLabel5,
  }

  const done = () => {
    store.next()
    setTimeout(() => {
      setLoading(false)
      setSent(true)
    }, SWIPER_SPEED_MS)
  }

  const send = () => {
    if (sent) return

    setLoading(true)
    setError(false)

    if (message.trim().length === 0) {
      setError(true)
      setLoading(false)
      return
    }

    sendMessage(message, store.selectedVote || '')
      .then((res) => {
        if (res && res.status === 201) {
          done()
        } else {
          setLoading(false)
        }
      })
      .catch(() => {
        setLoading(false)
      })
  }

  function onChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setMessage(event.target.value)
    setError(false)
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6} className={classes.textContent}>
          <Headline level={2} className={classes.headline}>
            {!!store.messageHeadlineHighlightedWord ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: store.messageHeadline.replace(
                    store.messageHeadlineHighlightedWord,
                    `<span class="highlight">${
                      items[store.selectedVote as keyof typeof items]
                    }</span>`
                  ),
                }}
              />
            ) : (
              store.messageHeadline
            )}
          </Headline>
          <p className={classes.copy}>{store.messageCopy}</p>
        </Grid>
        <Grid item xs={12} md={6}>
          <div className={classes.textAreaRoot}>
            <label htmlFor="message-textarea" className={classes.label}>
              {store.messageTextareaPlaceholder}
            </label>
            <textarea
              id="message-textarea"
              placeholder={store.messageTextareaPlaceholder}
              className={clsx(classes.textAreaInput, {
                [classes.textAreaInputError]: error,
              })}
              name="message"
              value={message}
              onChange={onChange}
              aria-invalid={error}
              aria-describedby={error ? errorId : undefined}
              maxLength={1024}
            />
            <Icon name="Pencil" className={classes.textAreaIcon} />
            {error && (
              <p
                id={errorId}
                className={classes.error}
                role="alert"
                aria-live="assertive"
              >
                {store.messageTextareaErrorMessage}
              </p>
            )}
          </div>
        </Grid>
      </Grid>
      <div className={classes.buttonWrapper}>
        <Button onClick={store.next} light className={classes.skipButton}>
          {store.messageSkipButtonLabel}
        </Button>
        <Button onClick={send} disabled={loading}>
          <div className={clsx({ [classes.hideLabel]: loading })}>
            {store.messageButtonLabel}
          </div>

          <div
            className={clsx(classes.spinner, {
              [classes.showSpinner]: loading,
            })}
          >
            <CircularProgress size={28} color="inherit" />
          </div>
        </Button>
      </div>
    </div>
  )
}
